<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import EchoInfoBox from '@/js/components/EchoInfoBox.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import EchoButton from '@/js/components/EchoButton.vue';
    import ToggleSwitch from '@/js/components/ToggleSwitch.vue';
    import PanelTabs from '@/js/components/PanelTabs.vue';
    import PanelTab from '@/js/components/PanelTab.vue';
    import SyncDataTypeSelection from '@/js/back-office/components/SyncDataTypeSelection.vue';
    import KeyIndicateSyncRunInfo from '@/js/back-office/components/KeyIndicateSyncRunInfo.vue';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import {
        useQueryGetMigratedTenants,
        useQueryGetKeyIndicateSyncSystems,
        useQueryGetKeyIndicateSyncDataTypes,
        useMutationSaveKeyIndicateSyncSystemSettings,
        useMutationSetKeyIndicateSyncDataTypeEnabled,
    } from '@/js/services/api/backOffice/backOfficeQueries';
    import {
        getKeyIndicateSyncDataTypeLabel,
        isValidDaysBack,
        type KeyIndicateSyncDataType,
        type KeyIndicateSyncDataTypeInfo,
        type KeyIndicateSyncSetting,
        type KeyIndicateSyncSystem,
    } from '@/js/services/api/backOffice/keyIndicateSyncSettingsApi';

    const successDialogStore = useSuccessDialog();
    const confirmationDialogStore = useConfirmationDialog();

    const selectedTenantId = ref('');
    const tenantSearch = ref<string | Option | null>(null);
    const activeTab = ref<string>();

    const { data: tenantsData } = useQueryGetMigratedTenants();
    const { data: systemsData } = useQueryGetKeyIndicateSyncSystems(selectedTenantId);
    const { data: dataTypesData } = useQueryGetKeyIndicateSyncDataTypes();

    const tenantOptions = computed<Option[]>(() =>
        (tenantsData.value ?? []).map((t) => ({ name: t.name, value: t.id }))
    );
    const systems = computed(() => (selectedTenantId.value ? (systemsData.value ?? []) : []));
    const dataTypes = computed(() => dataTypesData.value ?? []);

    // One row per data type, so types the system has no setting for yet can be turned on.
    const toDraft = (system: KeyIndicateSyncSystem): KeyIndicateSyncSetting[] =>
        dataTypes.value.map(
            (t) =>
                system.settings.find((s) => s.dataType === t.dataType) ?? {
                    dataType: t.dataType,
                    isEnabled: false,
                    daysBack: t.defaultDaysBack,
                }
        );

    const drafts = ref<Record<string, KeyIndicateSyncSetting[]>>({});

    // Untouched systems follow the server, so a refetch never leaves a stale draft to save over newer values.
    const editedSystemIds = new Set<string>();

    watch(
        [systems, dataTypes],
        () => {
            drafts.value = Object.fromEntries(
                systems.value.map((s) => {
                    const existing = drafts.value[s.tenantExternalSystemId];
                    const keepsEdits = editedSystemIds.has(s.tenantExternalSystemId) && existing;
                    return [s.tenantExternalSystemId, keepsEdits ? existing : toDraft(s)];
                })
            );
        },
        { immediate: true }
    );

    const updateDraft = (tenantExternalSystemId: string, draft: KeyIndicateSyncSetting[]) => {
        drafts.value[tenantExternalSystemId] = draft;
        editedSystemIds.add(tenantExternalSystemId);
    };

    const PER_CUSTOMER_TAB = 'per-customer';
    const outerTab = ref(PER_CUSTOMER_TAB);
    const selectedTenantOption = ref<Option | null>(null);

    const onTenantSelect = (option: Option) => {
        selectedTenantId.value = option.value;
        selectedTenantOption.value = option;
        activeTab.value = undefined;
    };
    const onTenantClear = () => {
        selectedTenantId.value = '';
        selectedTenantOption.value = null;
    };

    // PanelTab unmounts a hidden tab, so the customer picker forgets its selection; hand it back on return.
    watch(outerTab, (tab) => {
        if (tab === PER_CUSTOMER_TAB && selectedTenantOption.value) {
            tenantSearch.value = selectedTenantOption.value;
        }
    });

    // Types the system has no row for are only sent once turned on, so untouched types stay without a row.
    const settingsToSave = (system: KeyIndicateSyncSystem) =>
        (drafts.value[system.tenantExternalSystemId] ?? []).filter(
            (s) => s.isEnabled || system.settings.some((e) => e.dataType === s.dataType)
        );

    const canSaveSystem = (system: KeyIndicateSyncSystem) =>
        settingsToSave(system).every((s) => isValidDaysBack(s.daysBack));

    const { mutateAsync: saveSystemSettings } = useMutationSaveKeyIndicateSyncSystemSettings();
    const savingSystemId = ref<string | null>(null);

    const saveSystem = async (system: KeyIndicateSyncSystem) => {
        const settings = settingsToSave(system);
        if (settings.length === 0) {
            return;
        }

        savingSystemId.value = system.tenantExternalSystemId;
        try {
            await saveSystemSettings({
                tenantId: selectedTenantId.value,
                tenantExternalSystemId: system.tenantExternalSystemId,
                settings,
            });
            // The refetch ran while the draft still counted as edited, so rebuild it from the saved data now.
            editedSystemIds.delete(system.tenantExternalSystemId);
            const savedSystem = systems.value.find(
                (s) => s.tenantExternalSystemId === system.tenantExternalSystemId
            );
            if (savedSystem) {
                drafts.value[system.tenantExternalSystemId] = toDraft(savedSystem);
            }
            successDialogStore.openDialog({ message: __('saved') });
        } finally {
            savingSystemId.value = null;
        }
    };

    const { mutateAsync: setDataTypeEnabled } = useMutationSetKeyIndicateSyncDataTypeEnabled();
    const togglingDataType = ref<KeyIndicateSyncDataType | null>(null);
    // The browser has already flipped the switch; re-rendering puts it back on cancel or error.
    const globalListKey = ref(0);

    const toggleGlobally = async (info: KeyIndicateSyncDataTypeInfo, isEnabled: boolean) => {
        if (!isEnabled) {
            const confirmed = await confirmationDialogStore.openDialog({
                message: __('back-office-key-indicate-sync-settings-global-off-confirm', {
                    dataType: getKeyIndicateSyncDataTypeLabel(info),
                }),
            });
            if (!confirmed) {
                globalListKey.value++;
                return;
            }
        }

        togglingDataType.value = info.dataType;
        try {
            await setDataTypeEnabled({ dataType: info.dataType, isEnabled });
        } catch {
            // The mutation's onError already shows the error.
            globalListKey.value++;
        } finally {
            togglingDataType.value = null;
        }
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-key-indicate-sync-settings') }}</h1>
        </template>
        <template #content>
            <EchoInfoBox class="mb-6 max-w-2xl">
                {{ __('back-office-key-indicate-sync-settings-info') }}
            </EchoInfoBox>

            <PanelTabs v-model="outerTab" class="w-full max-w-3xl">
                <PanelTab
                    :value="PER_CUSTOMER_TAB"
                    :label="__('back-office-key-indicate-sync-settings-per-customer-tab')"
                >
                    <div class="flex flex-col gap-6">
                        <div class="w-lg max-w-full">
                            <AutoComplete
                                v-model="tenantSearch"
                                input-id="key-indicate-sync-settings-tenant"
                                :label="__('back-office-select-customer')"
                                :options="tenantOptions"
                                @select="onTenantSelect"
                                @clear="onTenantClear"
                            />
                        </div>

                        <p
                            v-if="selectedTenantId && systemsData && systems.length === 0"
                            class="text-body text-gray-medium"
                        >
                            {{ __('back-office-key-indicate-sync-settings-no-systems') }}
                        </p>

                        <PanelTabs v-if="systems.length > 0" v-model="activeTab">
                            <PanelTab
                                v-for="system in systems"
                                :key="system.tenantExternalSystemId"
                                :value="system.tenantExternalSystemId"
                                :label="system.systemName"
                            >
                                <div class="flex flex-col gap-4">
                                    <KeyIndicateSyncRunInfo :system="system" />
                                    <SyncDataTypeSelection
                                        :model-value="drafts[system.tenantExternalSystemId] ?? []"
                                        :data-types="dataTypes"
                                        :id-prefix="`key-indicate-sync-settings-${system.tenantExternalSystemId}`"
                                        @update:model-value="
                                            updateDraft(system.tenantExternalSystemId, $event)
                                        "
                                    />
                                    <div>
                                        <EchoButton
                                            type="button"
                                            variant="primary"
                                            :disabled="!canSaveSystem(system)"
                                            :loading="
                                                savingSystemId === system.tenantExternalSystemId
                                            "
                                            @click="saveSystem(system)"
                                        >
                                            {{ __('save') }}
                                        </EchoButton>
                                    </div>
                                </div>
                            </PanelTab>
                        </PanelTabs>
                    </div>
                </PanelTab>
                <PanelTab
                    value="all-customers"
                    :label="__('back-office-key-indicate-sync-settings-global-tab')"
                >
                    <div class="flex flex-col gap-4">
                        <p class="text-body text-error">
                            {{ __('back-office-key-indicate-sync-settings-global-info') }}
                        </p>
                        <div :key="globalListKey" class="flex flex-col gap-2">
                            <div
                                v-for="info in dataTypes"
                                :key="info.dataType"
                                class="flex items-center gap-3"
                            >
                                <span class="text-body flex-1">
                                    {{ getKeyIndicateSyncDataTypeLabel(info) }}
                                </span>
                                <ToggleSwitch
                                    :model-value="info.isEnabled"
                                    :disabled="togglingDataType === info.dataType"
                                    :aria-label="getKeyIndicateSyncDataTypeLabel(info)"
                                    @update:model-value="toggleGlobally(info, $event ?? false)"
                                />
                                <span class="text-body w-16">
                                    {{ info.isEnabled ? __('active') : __('inactive') }}
                                </span>
                            </div>
                        </div>
                    </div>
                </PanelTab>
            </PanelTabs>
        </template>
    </SettingsPageLayout>
</template>
